import { createPluginViewStateAdapter } from './createPluginViewStateAdapter';
import getBottomDockingHeight from './getBottomDockingHeight';
import createAttachments from 'owa-calendar-compose-form-store/lib/actions/createAttachments';
import getCurrentEventOrganizer from 'owa-calendar-compose-form-store/lib/selectors/getCurrentEventOrganizer';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import { getAttendeeLists } from 'owa-calendar-compose-form-store/lib/utils/getAttendees';
import getEventClientItemId from 'owa-calendar-compose-form-store/lib/utils/getEventClientItemId';
import { getAllRecipientsFromAttendees } from 'owa-calendar-compose-form-store/lib/utils/getRecipientsFromAttendees';
import { getComposeEvent, getOriginalEvent } from 'owa-calendar-compose-store-manager';
import { isEventAMeeting, isFTDFSeriesMaster } from 'owa-calendar-event-capabilities';
import { GetAnnotationHandlerAsync, LazyAugLoopPluginClass } from 'owa-editor-augloop-plugin';
import { ComposeScenario } from 'owa-editor-augloop-plugin/lib/store/schema/ComposeScenario';
import DockingPlugin from 'owa-editor-docking-plugin/lib/plugin/DockingPlugin';
import { LazyEmojiPickerPluginClass } from 'owa-editor-emoji-picker-plugin';
import { LazyInlineImagePluginClass } from 'owa-editor-inlineimage-plugin';
import { LazyLinkPluginClass } from 'owa-editor-link-plugin';
import { LazyAtMentionsPickerPluginClass } from 'owa-editor-mentions-picker-plugin';
import { LazyPasteOptionPluginClass } from 'owa-editor-paste-option-plugin';
import { isProofingSupported } from 'owa-editor-proofing-common';
import { LazyProofingPluginClass } from 'owa-editor-proofing-plugin';
import SpellcheckPlugin from 'owa-editor-proofing-plugin/lib/plugin/SpellcheckPlugin';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { LazyTableEditPluginClass } from 'owa-editor-table-edit-plugin';
import { getAnnotationHandlerAsync as getTextPredictionHandlerAsync } from 'owa-editor-textprediction-handler';
import { LazyTopicPickerPluginClass, isTopicPickerEnabled } from 'owa-editor-topic-picker-plugin';
import { LazyFluidPluginClass } from 'owa-fluid-integrations';
import { FluidOwaSource, isFluidEnabledForSource } from 'owa-fluid-validations';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    addPendingInlineImage,
    getPendingInlineImages,
} from 'owa-calendar-compose-form-store/lib/utils/pendingInlineImages';
import type { FullComposeEditorPlugins, FullComposeViewState } from 'owa-calendar-compose-types';
import {
    addAttendeeFromEmailAddress,
    onIsDraftChanged,
    saveEventDraft,
    addPersonaToRequiredAttendees,
    removePersonaFromRequiredAttendees,
} from 'owa-calendar-compose-form-actions';
import type { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import type { AttachmentFileList } from 'owa-attachment-file-types';
import type FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import {
    getTextPredictionFlightState,
    TextPredictionViaALPlugin,
} from 'owa-proofing-option/lib/utils/getTextPredictionFlightState';
import {
    LazySonoraMentionsPluginClass,
    isSonoraMentionsPluginEnabledForCalendar as isSonoraMentionsPluginEnabledFunc,
} from 'owa-editor-sonora-mentions-picker-plugin';
import getInsertLinksHost from './getInsertLinksHost';

function getEmailReferenceId() {
    // VSO 122516: For ReplyByMeeting, figure out how to get referenceItemId.
    // For now just return null.
    return null;
}

function getSubjectHandler(formId: string): () => string {
    return () => {
        const event = getComposeEvent(formId);
        return event?.Subject || '';
    };
}

function getAttendeesAsRecipientsHandler(formId: string) {
    return function () {
        // The return type of this function must match the 'Recipients' type
        // in owa-editor-augloop-plugin package.
        const event = getComposeEvent(formId);

        if (!event) {
            return {
                toRecipients: null,
                ccRecipients: null,
                bccRecipients: null,
            };
        }

        // We map required attendees to the To list and optional
        // attendees to the cc list. bcc list always left blank.
        const requiredAttendees = event.RequiredAttendees
            ? event.RequiredAttendees.map(attendee => attendee.Mailbox)
            : [];

        const optionalAttendees = event.OptionalAttendees
            ? event.OptionalAttendees.map(attendee => attendee.Mailbox)
            : [];

        return {
            toRecipients: requiredAttendees,
            ccRecipients: optionalAttendees,
            bccRecipients: null,
        };
    };
}

export default function createFullComposeEditorPlugins(
    viewState: FullComposeViewState,
    formId: string
): FullComposeEditorPlugins {
    const event = getComposeEvent(formId);
    const originalEvent = getOriginalEvent(formId);
    const canModifyRecipients = !(isFTDFSeriesMaster(event) && isEventAMeeting(originalEvent));
    const editorAdapter = createPluginViewStateAdapter(viewState, 'bodyViewState', formId);
    const isRtl = isCurrentCultureRightToLeft();
    const attachmentWellViewStateAdapter = createPluginViewStateAdapter(
        viewState,
        'attachmentWell',
        formId
    );
    const attachmentHandler = createAttachmentHandler(viewState, formId);

    const onAddAttendeeFromEmailAddress = (address: string): void => {
        addAttendeeFromEmailAddress(address, formId);
    };

    const onAddPersonaToRequiredAttendees = (persona: FindRecipientPersonaType) => {
        addPersonaToRequiredAttendees(persona, formId);
    };

    const onRemovePersonaFromRequiredAttendees = (persona: FindRecipientPersonaType) => {
        removePersonaFromRequiredAttendees(persona, formId);
    };

    const onGetAttendeeLists = () => {
        return getAttendeeLists(formId);
    };

    const onGetAllRecipientsFromAttendees = () => {
        return getAllRecipientsFromAttendees(formId);
    };

    const onGetBottomDockingHeight = () => {
        return getBottomDockingHeight(formId);
    };

    const augmentationLoopHandlers: GetAnnotationHandlerAsync[] = [];
    let augLoopFeatureEnabled: boolean = false;
    if (getTextPredictionFlightState() & TextPredictionViaALPlugin) {
        augmentationLoopHandlers.push(getTextPredictionHandlerAsync);
        augLoopFeatureEnabled = true;
    }

    const augLoopSessionViewState = createPluginViewStateAdapter(
        viewState,
        'augloopSession',
        formId
    );
    const sonoraMentionsViewState = createPluginViewStateAdapter(
        viewState,
        'sonoraMentions',
        formId
    );
    const isSonoraMentionsPluginEnabled = isSonoraMentionsPluginEnabledFunc();

    // NOTE: If the plugin has a parameter that is a function on the view state,
    // use createPluginViewStateAdapter so the current view state is retrieved
    // at the time it is called (VSO 54506)
    return {
        docking: new DockingPlugin(
            createPluginViewStateAdapter(viewState, 'docking', formId),
            editorAdapter,
            onGetBottomDockingHeight
        ),
        ribbon: new RibbonPlugin(
            createPluginViewStateAdapter(viewState, 'ribbon', formId),
            editorAdapter
        ),
        emojiPicker: new LazyEmojiPickerPluginClass(
            createPluginViewStateAdapter(viewState, 'emojiPicker', formId),
            editorAdapter,
            false /*useFlexPane*/,
            viewState.expressionId
        ),
        spellcheckPlugin: new SpellcheckPlugin(),
        inlineImageV2: new LazyInlineImagePluginClass(
            createPluginViewStateAdapter(viewState, 'inlineImageV2', formId),
            editorAdapter,
            attachmentWellViewStateAdapter,
            attachmentHandler,
            false /*isInlineIMageDisabled*/,
            event.ParentFolderId.mailboxInfo.userIdentity
        ),
        link: new LazyLinkPluginClass(
            viewState.bodyViewState,
            viewState.attachmentWell,
            onGetAttendeeLists,
            getFromAddress(formId),
            true /* isCalendar */,
            false /* allowCtrlK */
        ),
        mentionsPicker: !isSonoraMentionsPluginEnabled
            ? new LazyAtMentionsPickerPluginClass(
                  createPluginViewStateAdapter(viewState, 'mentionsPicker', formId),
                  editorAdapter,
                  onAddPersonaToRequiredAttendees,
                  onRemovePersonaFromRequiredAttendees,
                  null,
                  canModifyRecipients,
                  onGetAllRecipientsFromAttendees
              )
            : null,
        topicPicker:
            isTopicPickerEnabled() &&
            new LazyTopicPickerPluginClass(
                createPluginViewStateAdapter(viewState, 'topicPicker', formId),
                editorAdapter
            ),
        proofing: isProofingSupported()
            ? new LazyProofingPluginClass(
                  createPluginViewStateAdapter(viewState, 'proofing', formId),
                  editorAdapter,
                  createPluginViewStateAdapter(viewState, 'augloopSession', formId),
                  'Calendar',
                  true /* isCalendar */,
                  viewState
              )
            : null,
        // TODO VSO 100597: support at mentions in fluid in calendar
        fluid: isFluidEnabledForSource(FluidOwaSource.AgendaEditor)
            ? new LazyFluidPluginClass(
                  viewState.bodyViewState,
                  FluidOwaSource.AgendaEditor,
                  onAddAttendeeFromEmailAddress,
                  viewState.collabSpaceViewStateId
              )
            : null,
        pasteOption: new LazyPasteOptionPluginClass(
            createPluginViewStateAdapter(viewState, 'pasteOption', formId),
            editorAdapter,
            isRtl
        ),
        tableEdit: new LazyTableEditPluginClass(),
        augLoop: augLoopFeatureEnabled
            ? new LazyAugLoopPluginClass(
                  createPluginViewStateAdapter(viewState, 'augLoop', formId),
                  editorAdapter,
                  augLoopSessionViewState,
                  isSonoraMentionsPluginEnabled ? sonoraMentionsViewState : null,
                  augmentationLoopHandlers,
                  getAttendeesAsRecipientsHandler(formId),
                  getSubjectHandler(formId),
                  getEmailReferenceId,
                  isNewEvent(formId) ? 'New' : 'Edit',
                  ComposeScenario.CalendarFull
              )
            : null,
        sonoraMentions: isSonoraMentionsPluginEnabled
            ? new LazySonoraMentionsPluginClass(
                  sonoraMentionsViewState,
                  editorAdapter,
                  false /* shouldShowHintOnInitialize */,
                  {
                      addSelectedPersonaToWell: onAddPersonaToRequiredAttendees,
                      removePersonaFromWell: onRemovePersonaFromRequiredAttendees,
                      onRecipientAdded: null,
                      canModifyRecipients: canModifyRecipients,
                      getAllRecipients: onGetAllRecipientsFromAttendees,
                  },
                  {
                      augloopSessionAdapter: augLoopSessionViewState,
                      attachmentWellAdapter: attachmentWellViewStateAdapter,
                      insertLinksHost: getInsertLinksHost(viewState, formId),
                      createAttachmentsCallback: attachmentHandler,
                      fromAddress: getFromAddress(formId),
                      getRecipients: getAttendeesAsRecipientsHandler(formId),
                      getSubject: getSubjectHandler(formId),
                  }
              )
            : null,
    };
}

function getFromAddress(formId: string): string {
    const organizer = getCurrentEventOrganizer(formId);
    return organizer
        ? organizer.Mailbox.EmailAddress
        : getUserConfiguration().SessionSettings.UserEmailAddress;
}

function createAttachmentHandler(viewState: FullComposeViewState, formId: string) {
    return (files: AttachmentFileList, properties: CreateAttachmentsProperties) => {
        let itemId = getEventClientItemId(formId);
        if (itemId?.Id) {
            createAttachments(viewState, getEventClientItemId(formId), files, properties, formId);
        } else {
            const hasPendingInlineImage = getPendingInlineImages().length > 0;

            for (let i = 0; i < files.length; i++) {
                addPendingInlineImage(files[i]);
            }

            if (!hasPendingInlineImage) {
                onIsDraftChanged(true /*isDraft*/, formId);
                saveEventDraft(formId);
            }
        }
    };
}
